import { gql } from 'apollo-angular';
import {
  AppointmentsBundles,
  CustomerBranding,
  CustomQuestionResponse,
  GuestApplication,
  QuestionResponseContainer
} from '@ui/shared/model';
import {
  appointmentFields,
  propertyPsSide,
  questionResponseContainerFields
} from '@ui/legacy-lib';
import { TypedDocumentNode } from '@apollo/client';
import { customerBrandingFields } from '@ui/tenant-pool/shared/user/data-access';
import { basicPropertySearcherFields } from './property-searcher.queries';

export interface GuestAppointmentsQueryResult {
  guestAppointments: AppointmentsBundles[];
}

export interface GuestApplicationQueryResult {
  guestApplication: GuestApplication;
}

export interface CustomQuestionResponsesQueryResult {
  customQuestionResponses: CustomQuestionResponse[];
}

export interface BrandingQueryResult {
  branding: CustomerBranding;
}

export interface GuestQuestionsQueryResponse {
  guestQuestions: QuestionResponseContainer;
}

export const responseStatusFields = `
  status
  statusText
`;

export const attachmentFields = `
  url
  title
  type
  identifier
  extension
  encrypted
  index
`;

export const guestAppointmentsQuery = gql`
  query guestAppointments($token: String!) {
    guestAppointments(token: $token) {
      ${appointmentFields}
    }
  }
`;

export const guestApplicationQuery = gql`
  query guestApplication($token: String!) {
    guestApplication(token: $token) {
      askForViewingAppointmentAttendance
      userProfile
      applicantStatus
      ${basicPropertySearcherFields}
      ${propertyPsSide}
      captchaSettings {
        siteKey
        whitelabelRegistrationProcessActive
        applicationSubmissionActive
      }
    }
  }
`;

export const brandingQuery: TypedDocumentNode<
  BrandingQueryResult,
  { token: string }
> = gql`
  query branding($token: String!) {
    branding(token: $token) {
      ${customerBrandingFields}
    }
  }
`;

export const customQuestionResponsesQuery: TypedDocumentNode<
  CustomQuestionResponsesQueryResult,
  { token: string }
> = gql`
  query customQuestionResponses($token: String!) {
    customQuestionResponses(token: $token) {
      responses
      selectedRange
      comment
      customQuestion {
        key
        type
        subType
        title
        options
        commentHint
      }
    }
  }
`;

export const appointmentAcceptancesCancelMutation = gql`
  mutation appointmentAcceptancesCancel($cancelAppointmentData: CancelAppointmentDataInput, $token: String!) {
    appointmentAcceptancesCancel(cancelAppointmentDataInput: $cancelAppointmentData, token: $token) {
      ${responseStatusFields}
    }
  }
`;

export const registerMutation = gql`
  mutation register(
    $userData: TenantRegisterInput!
    $token: String!
    $captchaResponse: String
  ) {
    register(
      input: $userData
      token: $token
      captchaResponse: $captchaResponse
    ) {
      ${responseStatusFields}
    }
  }
`;

export const applicationRegisterMutation = gql`
  mutation applicationRegister(
    $userData: GuestRegisterInput!
    $token: String!
    $captchaResponse: String
  ) {
    applicationRegister(
      input: $userData
      token: $token
      captchaResponse: $captchaResponse
    ) {
      ${responseStatusFields}
    }
  }
`;

export const deleteQuery = gql`
  mutation delete($reasonData: ReasonInput, $token: String!) {
    delete(input: $reasonData, token: $token) {
      ${responseStatusFields}
    }
  }
`;

export const acceptAppointmentMutation = gql`
  mutation acceptAppointment($id: ID!, $token: String!) {
    acceptAppointment(id: $id, token: $token) {
      ${responseStatusFields}
    }
  }
`;

export const declareIntentMutation = gql`
  mutation guestDeclareIntent($declareIntentData: DeclareIntentInput!, $token: String!) {
    guestDeclareIntent(input: $declareIntentData, token: $token) {
      ${responseStatusFields}
    }
  }
`;

export const guestQuestionsQuery = gql`
  query guestQuestions($token: String!) {
    guestQuestions(token: $token) {
      ${questionResponseContainerFields}
    }
  }
`;
